import { apiServer } from "../config";
import { SociabilityHighlight } from "../types/nearby-businesses";
import { SearchApiParams } from "../types/search";
import User from "../types/user";
import { OpeningHour, Venue } from "../types/venue";
import { fetchWithToken } from "./fetchWithToken";
import { resolveBrandFields } from "../utils/resolveBrandFields";

/**
 * Calculate zoom level based on search distance in kilometers
 * @param distanceInKm Distance in kilometers
 * @returns Appropriate zoom level (12-22)
 */
export const calculateZoomFromDistance = (distanceInKm: number): number => {
  // Default to zoom level 15 if distance is not provided
  if (!distanceInKm) return 16;

  // Map distance ranges to zoom levels
  if (distanceInKm >= 10) return 12;
  if (distanceInKm >= 5) return 13;
  if (distanceInKm >= 2) return 14;
  if (distanceInKm >= 1) return 15;
  return 16;
};

export type BrandItem = {
  value: string;
  logo?: string;
  count: number;
};

export type FilteredVenue = {
  name: string;
  types: string[];
  reason: string;
  match_quality: string;
  position: number;
  place_id: string;
  business_status?: string;
};

export type LoadLocationsResponse = {
  locations: Venue[];
  accessibility_tags: any[];
  newRegion?: {
    latitude: number;
    longitude: number;
    latitudeDelta: number;
    longitudeDelta: number;
  };
  newLocation?: {
    lattitude: number;
    longitude: number;
  };
  search_original_distance?: number;
  search_distance_used?: number;
  facets: Record<string, BrandItem[]>;
  total_count?: number;
  filtered_venues?: FilteredVenue[];
};

export async function loadLocations(
  accessToken: string | null | undefined,
  params: SearchApiParams,
): Promise<LoadLocationsResponse> {
  const parameters = {
    search: params.search,
    latitude: params.latitude || "",
    longitude: params.longitude || "",
    userlatitude: params.userlatitude || "",
    userlongitude: params.userlongitude || "",
    showVenueOptions: params.showVenueOptions,
    addinfo: params.addinfo,
    placesToReplaceString: params.placesToReplaceString,
    searchThisAreaClicked: params.searchThisAreaClicked,
    zoom: params.zoom,
    new_map: params.new_map,
    tagging_mode: params.tagging_mode,
    progressive_update: params.progressive_update,
    saved_place_id: params.saved_place_id,
    saved_place_user_id: params.saved_place_user_id,
    saved_place_sortby: params.saved_place_sortby,
    ...(params.venue_id !== undefined && { venue_id: params.venue_id }),
    ...(params.search_type !== undefined && {
      search_type: params.search_type,
    }),
    ...(params.search_value !== undefined && {
      search_value: params.search_value,
    }),
    ...(params.types !== undefined && { types: params.types }),
    ...(params.accessibilities !== undefined && {
      accessibilities: params.accessibilities,
      unixTimestamp: params.unixTimestamp?.toString(),
    }),
    ...(params.includeGoogleFallback !== undefined && {
      includeGoogleFallback: params.includeGoogleFallback,
    }),
    // Opening hours filters for custom mode
    ...(params.openingHoursDay !== undefined && {
      openingHoursDay: params.openingHoursDay,
    }),
    ...(params.openingHoursTime !== undefined && {
      openingHoursTime: params.openingHoursTime,
    }),
    ...(params.mapping_status !== undefined && {
      mapping_status: params.mapping_status,
    }),
    ...(params.map_ne_lat !== undefined && { map_ne_lat: params.map_ne_lat }),
    ...(params.map_ne_lng !== undefined && { map_ne_lng: params.map_ne_lng }),
    ...(params.map_sw_lat !== undefined && { map_sw_lat: params.map_sw_lat }),
    ...(params.map_sw_lng !== undefined && { map_sw_lng: params.map_sw_lng }),
    ...(params.per_page !== undefined && { per_page: params.per_page }),
  };

  const queryString = new URLSearchParams(parameters).toString();
  const urlWithParams = `${apiServer + "/business/search"}?${queryString}`;

  console.log(
    parameters.tagging_mode === "on"
      ? "Tagging Search parameters:"
      : "Home Search parameters:",
    parameters,
    urlWithParams,
  );
  const startTime = Date.now();
  const response = await fetch(urlWithParams, {
    method: "GET",
    headers: {
      Authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  });
  const endTime = Date.now(); // End timing

  const duration = endTime - startTime; // Calculate duration
  // console.log(`Fetch call took ${duration} milliseconds.`, queryString);

  const data = await response.json();
  if (data.data) {
    const locations = convertLocations(data.data);
    return {
      locations,
      accessibility_tags: data.filter_accessibility,
      search_original_distance: data.search_original_distance,
      search_distance_used: data.search_distance_used,
      facets: data.facets,
      total_count: data.total_count,
      filtered_venues: data.filtered_venues || [],
    };
  } else {
    console.error("loadLocations error", data, urlWithParams);
    return {
      locations: [],
      accessibility_tags: [],
      search_original_distance: 0,
      search_distance_used: 0,
      facets: {},
      total_count: 0,
      filtered_venues: [],
    };
  }
}

export type PlaceFromGoogleResult =
  | { status: "success"; created: boolean; venue: Venue }
  | {
      status: "supported";
      placeId: string;
      name: string;
      businessType: string;
      address: string;
      lat?: number | null;
      lng?: number | null;
    }
  | { status: "unsupported"; message: string; lat?: number | null; lng?: number | null }
  | { status: "error"; message: string };

/**
 * Resolve a clicked Google POI (place_id) into a Grey Pin venue, or an
 * "unsupported" status for categories we don't support.
 */
export async function createPlaceFromGoogle(
  placeId: string,
  accessToken?: string | null,
  sessionToken?: string | null,
  // lookup: classify the place (existing/supported/unsupported) WITHOUT creating it.
  lookup?: boolean,
): Promise<PlaceFromGoogleResult> {
  try {
    const response = await fetch(`${apiServer}/business/place-from-google`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      // sessiontoken closes the autocomplete session opened by the search bar.
      body: JSON.stringify({
        place_id: placeId,
        ...(sessionToken ? { sessiontoken: sessionToken } : {}),
        ...(lookup ? { lookup: true } : {}),
      }),
    });

    const data = await response.json();

    if (data.status === "success" && data.data?.venue) {
      const venue = convertToLocation(data.data.venue);
      if (venue) {
        return {
          status: "success",
          created: !!data.data.created,
          venue,
        };
      }
    }

    // Lookup only: supported type, not yet in our DB (nothing created).
    if (data.status === "supported") {
      return {
        status: "supported",
        placeId: data.data?.place_id ?? placeId,
        name: data.data?.name ?? "",
        businessType: data.data?.business_type ?? "",
        address: data.data?.address ?? "",
        lat: data.data?.lat ?? null,
        lng: data.data?.lng ?? null,
      };
    }

    if (data.status === "unsupported") {
      return {
        status: "unsupported",
        message: data.message || "This place type is not supported.",
        lat: data.data?.lat ?? null,
        lng: data.data?.lng ?? null,
      };
    }

    return {
      status: "error",
      message: data.message || "Could not add this place.",
    };
  } catch (error) {
    console.error("createPlaceFromGoogle error", error);
    return { status: "error", message: "Could not add this place." };
  }
}

export type EnrichedPlacePrediction = {
  place_id: string;
  description: string;
  mainText: string;
  /** Backend-assigned type: "google_place" for venues, "google_area" for areas/neighbourhoods, "google_station" for transit stops, "google_airport" for airports */
  suggestionType?: "google_place" | "google_area" | "google_station" | "google_airport";
  secondaryText: string;
  types: string[];
  distanceMeters: number | null;
  inDb: boolean;
  hasAccessibility: boolean;
  pinType: "orange" | "grey" | null;
  slug: string | null;
  businessId: number | null;
};

/**
 * Google place autocomplete predictions annotated with whether each place is
 * already in our system (and its pin type / slug). Returns [] when disabled/empty.
 */
export async function getEnrichedPlacePredictions(
  input: string,
  options: {
    latitude?: number;
    longitude?: number;
    language?: string;
    sessionToken?: string;
    accessToken?: string | null;
  } = {},
): Promise<EnrichedPlacePrediction[]> {
  if (!input || input.trim().length < 2) return [];
  try {
    const params = new URLSearchParams();
    params.set("input", input);
    if (options.latitude !== undefined && options.longitude !== undefined) {
      params.set("location", `${options.latitude},${options.longitude}`);
    }
    if (options.language) params.set("language", options.language);
    if (options.sessionToken) params.set("sessiontoken", options.sessionToken);

    const res = await fetch(
      `${apiServer}/business/places-autocomplete-enriched?${params.toString()}`,
      {
        headers: {
          Accept: "application/json",
          ...(options.accessToken
            ? { Authorization: `Bearer ${options.accessToken}` }
            : {}),
        },
      },
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data?.data?.predictions ?? []) as EnrichedPlacePrediction[];
  } catch (error) {
    console.error("getEnrichedPlacePredictions error", error);
    return [];
  }
}

// Unified auto-suggest (GET /business/search-suggest-unified): one call returns
// render-ready items, each carrying an `action` the client dispatches on tap.

export type UnifiedSuggestAction =
  | {
      type: "OPEN_VENUE";
      placeId: string | null;
      slug: string | null;
      businessId: number | string | null;
    }
  | { type: "CREATE_GREY_PIN"; placeId: string }
  | { type: "PAN_AND_NEARBY"; placeId: string }
  | {
      type: "CATEGORY_IN_AREA";
      locationQuery: string;
      businessType: string | null;
      fallbackText: string;
    }
  | { type: "BUSINESS_TYPE"; businessType: string | null }
  | { type: "BRAND"; brand: string | null }
  | {
      type: "TEXT_SEARCH";
      text: string;
      searchType?: string;
      searchValue?: string;
    };

export type UnifiedSuggestData = {
  place_id: string | null;
  slug: string | null;
  businessId: number | string | null;
  inDb: boolean;
  pinType: "orange" | "grey" | null;
  distanceMeters: number | null;
  latitude: number | null;
  longitude: number | null;
  businessType: string | null;
  brand: string | null;
  suggestion_type: string;
  locationQuery?: string;
};

export type UnifiedSuggestItem = {
  displayText: string;
  subtitle: string;
  /** Semantic icon key ("cafe"/"venue"/"brand"/"area"/"station"/"airport"/"search"/"location"); in-DB venues prefer data.pinType. */
  iconKey: string;
  /** Character [start, end] ranges into displayText to bold. */
  highlightRanges: [number, number][];
  action: UnifiedSuggestAction;
  data: UnifiedSuggestData;
};

export async function getUnifiedSuggestions(
  term: string,
  options: {
    latitude?: number;
    longitude?: number;
    radius?: number;
    language?: string;
    sessionToken?: string;
    tagging_mode?: string;
    types?: string;
    accessibilities?: string;
    showVenueOptions?: string;
    accessToken?: string | null;
  } = {},
): Promise<UnifiedSuggestItem[]> {
  if (!term || term.trim().length < 2) return [];
  try {
    const params = new URLSearchParams();
    params.set("term", term);
    if (options.latitude !== undefined)
      params.set("latitude", String(options.latitude));
    if (options.longitude !== undefined)
      params.set("longitude", String(options.longitude));
    if (options.radius !== undefined)
      params.set("radius", String(options.radius));
    if (options.language) params.set("language", options.language);
    if (options.sessionToken) params.set("sessiontoken", options.sessionToken);
    if (options.tagging_mode !== undefined)
      params.set("tagging_mode", options.tagging_mode);
    if (options.types !== undefined) params.set("types", options.types);
    if (options.accessibilities !== undefined)
      params.set("accessibilities", options.accessibilities);
    if (options.showVenueOptions !== undefined)
      params.set("showVenueOptions", options.showVenueOptions);

    const res = await fetch(
      `${apiServer}/business/search-suggest-unified?${params.toString()}`,
      {
        headers: {
          Accept: "application/json",
          ...(options.accessToken
            ? { Authorization: `Bearer ${options.accessToken}` }
            : {}),
        },
      },
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data?.data?.items ?? []) as UnifiedSuggestItem[];
  } catch (error) {
    console.error("getUnifiedSuggestions error", error);
    return [];
  }
}

export type ResolvedSuggestionArea = {
  place_id: string | null;
  lat: number;
  lng: number;
  name: string;
  address: string;
};

/**
 * Resolve a PAN_AND_NEARBY / CATEGORY_IN_AREA action to coordinates server-side
 * (closing the session token). Pass place_id (areas) or query (compound). Null on failure.
 */
export async function resolveSuggestionArea(
  options: {
    placeId?: string | null;
    query?: string | null;
    sessionToken?: string | null;
    language?: string;
    // Location bias so an ambiguous query (e.g. "waterloo") resolves near the map centre.
    latitude?: number | null;
    longitude?: number | null;
  } = {},
): Promise<ResolvedSuggestionArea | null> {
  if (!options.placeId && !options.query) return null;
  try {
    const params = new URLSearchParams();
    if (options.placeId) params.set("place_id", options.placeId);
    if (options.query) params.set("query", options.query);
    if (options.sessionToken) params.set("sessiontoken", options.sessionToken);
    if (options.language) params.set("language", options.language);
    if (
      options.latitude !== undefined &&
      options.latitude !== null &&
      options.longitude !== undefined &&
      options.longitude !== null
    ) {
      params.set("latitude", String(options.latitude));
      params.set("longitude", String(options.longitude));
    }

    const res = await fetch(
      `${apiServer}/business/resolve-suggestion-area?${params.toString()}`,
      { headers: { Accept: "application/json" } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.status !== "success" || !data?.data) return null;
    const d = data.data;
    if (typeof d.lat !== "number" || typeof d.lng !== "number") return null;
    return {
      place_id: d.place_id ?? null,
      lat: d.lat,
      lng: d.lng,
      name: d.name ?? "",
      address: d.address ?? "",
    };
  } catch (error) {
    console.error("resolveSuggestionArea error", error);
    return null;
  }
}

/** Close the autocomplete session with a free Details call (e.g. in-DB venue select). */
export async function closeSuggestSession(
  placeId: string,
  sessionToken: string,
): Promise<void> {
  if (!placeId || !sessionToken) return;
  try {
    const params = new URLSearchParams();
    params.set("place_id", placeId);
    params.set("sessiontoken", sessionToken);
    await fetch(
      `${apiServer}/business/close-suggest-session?${params.toString()}`,
      { headers: { Accept: "application/json" } },
    );
  } catch (error) {
    console.error("closeSuggestSession error", error);
  }
}

export async function getFirstResultLocationAndAddress(
  term: string,
  searchedLatitude: number,
  searchLongitude: number,
) {
  try {
    // Constructing the query parameters
    const queryParams = new URLSearchParams({
      searchTerm: term,
      searchedLatitude: searchedLatitude.toString(),
      searchedLongitude: searchLongitude.toString(),
    });

    // Adding query parameters to the URL
    const url = `${apiServer}/business/search-places?${queryParams.toString()}`;

    console.log(`Fetching data from ${url}`);
    // Fetching data using GET request
    const response = await fetch(url, {
      method: "GET",
    });

    // Check if response is ok before parsing JSON
    if (!response.ok) {
      throw new Error(
        `API returned status ${response.status}: ${response.statusText}`,
      );
    }

    // Check if response has content
    const text = await response.text();
    if (!text) {
      throw new Error("API returned empty response");
    }

    const data = JSON.parse(text);
    const items = data.items;
    if (items.length === 0) {
      throw new Error("No results found");
    }
    
    // Use first item for coordinates and resultType
    const firstItem = items[0];
    const itemLat = firstItem.position.lat;
    const itemLng = firstItem.position.lng;

    // Function to sanitize text - remove problematic characters
    const sanitizeText = (text: string): string => {
      return text
        .replace(/'/g, '') // Remove apostrophes
        .replace(/"/g, '') // Remove quotes
        .replace(/[^\w\s,-]/g, '') // Remove special chars except word chars, spaces, commas, hyphens
        .trim();
    };

    // Merge address components from all results to create comprehensive placesToReplaceString
    const addressComponents = {
      keywords: new Set<string>(),
      cities: new Set<string>(),
      counties: new Set<string>(),
      streets: new Set<string>(),
      states: new Set<string>(),
      districts: new Set<string>(),
      countries: new Set<string>(),
    };

    // Result types that should include the original search keyword
    const specificResultTypes = [
      'street',
      'locality',
      'administrativeArea',
      'houseNumber',
      'addressBlock',
      'intersection',
      'postalCodePoint',
    ];

    // Add search keyword if resultType is specific
    if (firstItem.resultType && specificResultTypes.includes(firstItem.resultType)) {
      const sanitizedTerm = sanitizeText(term);
      if (sanitizedTerm) {
        addressComponents.keywords.add(sanitizedTerm);
      }
    }

    // Collect unique sanitized values - only from UK results to avoid false positives
    items.forEach((item: any) => {
      const addr = item.address;
      // if (addr && (addr.countryCode === "GBR" || addr.countryName === "United Kingdom")) {
        if (addr.city) {
          const sanitized = sanitizeText(addr.city);
          if (sanitized) addressComponents.cities.add(sanitized);
        }
        if (addr.county) {
          const sanitized = sanitizeText(addr.county);
          if (sanitized) addressComponents.counties.add(sanitized);
        }
        if (addr.street) {
          const sanitized = sanitizeText(addr.street);
          if (sanitized) addressComponents.streets.add(sanitized);
        }
        if (addr.state) {
          const sanitized = sanitizeText(addr.state);
          if (sanitized) addressComponents.states.add(sanitized);
        }
        if (addr.district) {
          const sanitized = sanitizeText(addr.district);
          if (sanitized) addressComponents.districts.add(sanitized);
        }
        if (addr.countryName) {
          const sanitized = sanitizeText(addr.countryName);
          if (sanitized) addressComponents.countries.add(sanitized);
        }
      // }
    });

    // Build address string with unique values in order: keyword, city, county, street, state, district, country
    const addressParts = [
      ...Array.from(addressComponents.keywords),
      ...Array.from(addressComponents.cities),
      ...Array.from(addressComponents.counties),
      ...Array.from(addressComponents.streets),
      ...Array.from(addressComponents.states),
      ...Array.from(addressComponents.districts),
      ...Array.from(addressComponents.countries),
    ];

    const addressString = addressParts.join(", ");
    
    // Get simple address from first result only
    const firstAddr = firstItem.address;
    const simpleAddressParts: string[] = [];
    if (firstAddr) {
      if (firstAddr.street) simpleAddressParts.push(sanitizeText(firstAddr.street));
      if (firstAddr.city) simpleAddressParts.push(sanitizeText(firstAddr.city));
      if (firstAddr.countryName) simpleAddressParts.push(sanitizeText(firstAddr.countryName));
    }
    const simpleAddress = simpleAddressParts.length > 0 
      ? simpleAddressParts.join(", ") 
      : addressString; // Fallback to full address if no simple parts found
    
    return {
      lat: itemLat,
      lng: itemLng,
      address: addressString,
      simpleAddress: simpleAddress,
    };
  } catch (error: any) {
    console.error(
      `getFirstResultLocationAndAddress error for "${term}":`,
      error,
    );
    // Return the input coordinates as fallback (user's current location or default)
    return {
      lat: searchedLatitude,
      lng: searchLongitude,
      address: term, // Use the search term as the address fallback
      simpleAddress: term, // Use the same for simple address
      error: true,
    };
  }
}

export type Suggestion = {
  id: string | null;
  slug: string | null;
  place_id?: string | null;
  brandId?: string | number | null;
  /** @deprecated Use brandId — kept while autocomplete still returns brand_id */
  brand_id?: string | number | null;
  name: string | null;
  address: string | null;
  city: string | null;
  business_type: string | null;
  latitude: number | null;
  longitude: number | null;
  completeness: string | null;
  text: string;
  distanceKm: number | null;
  distanceText: string | null;
  suggestion_type?:
    | "venue_name"
    | "location"
    | "business_type"
    | "brand"
    | "google_place"
    | "google_area"
    | "google_station"
    | "google_airport"
    | string;
  // Enrichment fields, populated for "google_place" suggestions.
  inDb?: boolean;
  pinType?: "orange" | "grey" | null;
  brand?: string | null;
  street_display?: string | null;
  city_display?: string | null;
  state_display?: string | null;
};

export async function getSuggestions(
  term: string,
  options: {
    highlights?: boolean;
    fuzzy?: boolean;
    latitude?: number;
    longitude?: number;
    radius?: number;
    size?: number;
    tagging_mode?: string;
    types?: string;
    accessibilities?: string;
    showVenueOptions?: string;
  } = {},
): Promise<Suggestion[]> {
  const params = new URLSearchParams();
  params.set("term", term);
  if (options.highlights !== undefined)
    params.set("highlights", String(options.highlights));
  if (options.fuzzy !== undefined) params.set("fuzzy", String(options.fuzzy));
  if (options.latitude !== undefined)
    params.set("latitude", String(options.latitude));
  if (options.longitude !== undefined)
    params.set("longitude", String(options.longitude));
  if (options.radius !== undefined)
    params.set("radius", String(options.radius));
  if (options.size !== undefined) params.set("size", String(options.size));
  if (options.tagging_mode !== undefined)
    params.set("tagging_mode", options.tagging_mode);
  if (options.types !== undefined) params.set("types", options.types);
  if (options.accessibilities !== undefined)
    params.set("accessibilities", options.accessibilities);
  if (options.showVenueOptions !== undefined)
    params.set("showVenueOptions", options.showVenueOptions);

  const url = `${apiServer + "/business/suggest"}?${params.toString()}`;
  console.log("getSuggestions", url);
  const res = await fetch(url);
  if (!res.ok) return [];
  return (await res.json()) as Suggestion[];
}

export function convertToLocation(json: any): Venue | null {
  if (!json) {
    return null;
  }
  const brand = resolveBrandFields(json);
  return {
    id: json.id,
    placeId: json.place_id,
    name: json.name,
    phone: json.phone,
    website: json.website,
    address: json.address,
    latitude: json.latitude,
    longitude: json.longitude,
    rating: json.rating,
    types: json.types,
    keepUpdated: json.keep_updated,
    businessStatus: json.business_status,
    what3words: json.what3words,
    businessVerified: json.business_verified,
    businessVerifiedDatetime: json.business_verified_datetime,
    openNow: json.open_now,
    reviewDetailsSubmitted: json.review_details_submitted,
    tagIds: json.tag_ids,
    detailsFetchedAt: json.details_fetched_at,
    placeidCheckedAt: json.placeid_checked_at,
    googlePhotosCount: json.google_photos_count,
    sociabilityVisible: json.sociability_visible,
    cityLocation: json.city_location,
    openCloseStatus: json.open_close_status,
    notExistCount: json.notExistCount,
    bluePinsCount: json.bluePinsCount,
    distance: json.distance,
    userDistance: json.user_distance,
    openingHours: mapOpeningHours(json.opening_hours),
    taggingCategories: json.taggingCategories,
    reviewsDetailsSubmitted: json.reviewsDetailsSubmitted,
    reviewsSubmitted: json.reviewsSubmitted,
    photo: json.photo,
    photoThumb: json.photo_thumb,
    altText: json.alt_text,
    businessBadges: json.businessBadges,
    hasNotesForMappers: json.hasNotesForMappers,
    isRejectedMapperNote: json.isRejectedMapperNote,
    isNotExistMapperNote: json.isNotExistMapperNote,
    brandName: brand.brandName,
    brandId: brand.brandId,
    googleReviewsCount: json.google_reviews_count,
    businessType: json.businessType,
    business: {
      ...json.business,
      templateId: json.business?.template_id,
      venue_summary: json.business?.venue_summary,
    },
    reviews: json.reviews,
    latestReviewDateUpdated: json.latest_review_date_updated,
    to_be_verified: json.to_be_verified,
    isNoteStatusPending: json.isNoteStatusPending,
    isReturnToVenue: json.isReturnToVenue,
    city: json.city,
    nearby_venues_count: json.nearby_venues_count,
  };
}

export function convertLocations(jsonArray: any[]): Venue[] {
  return jsonArray
    .map((json) => convertToLocation(json))
    .filter((business) => business !== null) as Venue[];
}

function mapOpeningHours(openingHours: any): OpeningHour[] {
  //{"close_time": 2300, "close_time_utc": 2300, "created_at": "2023-11-27 07:33:42", "day": 0, "google_business_id": "f2cc05dc37b267d588dd8fe890bb3fe73a8bb2b4", "id": 2282936, "mode": 0, "open_time": 700, "open_time_utc": 700, "updated_at": "2023-11-27 07:33:42", "utc_offset": 0}
  return (
    openingHours?.map((openingHour: any) => ({
      closeTime: openingHour.close_time,
      closeTimeUtc: openingHour.close_time_utc,
      createdAt: openingHour.created_at,
      day: openingHour.day,
      googleBusinessId: openingHour.google_business_id,
      id: openingHour.id,
      mode: openingHour.mode,
      openTime: openingHour.open_time,
      openTimeUtc: openingHour.open_time_utc,
      updatedAt: openingHour.updated_at,
      utcOffset: openingHour.utc_offset,
    })) || []
  );
}

export async function getSociabilityHighlights(): Promise<
  SociabilityHighlight[]
> {
  const response = await fetch(`${apiServer}/splash-page/load`);
  const data = await response.json();
  return data.data as SociabilityHighlight[];
}

export interface NeighbourhoodSuggestionResult {
  count: number;
  searches: {
    id: number;
    user_id: number;
    search_term: string;
    filters: {
      search_latitude: any;
      search_longitude: any;
      search_radius: any;
      business_types: string[];
      accessibilities: string[];
      open_now: boolean;
      opening_hours: {
        start: string;
        end: string;
      };
    };
    saved_search_type: string;
    name: string;
    description: string;
    is_active: boolean;
    is_public: boolean;
    share_token: string;
    visibility: string[];
    total_results: number;
    created_at: string;
    updated_at: string;
    user: {
      id: number;
      name: string;
      email: string;
      email_verification: number;
      status: number;
      updated_at: string;
      stripe_id: any;
      card_brand: any;
      total_points: number;
      last_login_at: any;
      updated_by_user: any;
      last_2hr_inactivity_email_sent: any;
      last_72hr_inactivity_email_sent: any;
      last_high_hr_inactivity_email_sent: any;
      deputy_employee_id: string;
    };
  }[];
}

export interface RecentSearchResult {
  id: number;
  user_id: number;
  search_term: string;
  filters: {
    business_types: string[] | string;
    accessibilities: number[];
    opening_hours?: {
      day: number[];
      start: string;
      end: string;
    };
    open_now: boolean;
    // New parameters stored by backend
    latitude?: string;
    longitude?: string;
    address?: string;
    search_type?: string;
    search_value?: string;
    tagging_mode?: string;
    placesToReplaceString?: string;
  };
  search_type: string;
  name: string;
  description: string;
  is_active: boolean;
  is_public: boolean;
  share_token: any;
  created_at: string;
  updated_at: string;
}

export async function getNeighbourhoodSuggestions(
  share_token?: string,
  latitude?: number | null,
  longitude?: number | null,
) {
  const params = new URLSearchParams();
  if (share_token) params.set("share_token", share_token);
  if (latitude != null && isFinite(latitude)) params.set("latitude", latitude.toString());
  if (longitude != null && isFinite(longitude)) params.set("longitude", longitude.toString());

  const query = params.toString();
  const url = query
    ? `${apiServer}/user/searches/curated-saved-list?${query}`
    : `${apiServer}/user/searches/curated-saved-list`;

  const response = await fetch(url);
  if (!response.ok) return { count: 0, searches: [] };
  const data = await response.json();
  if (data.status !== "success") return { count: 0, searches: [] };
  return {
    count: data.data.count,
    searches: data.data.searches,
  } as NeighbourhoodSuggestionResult;
}

export interface RecentSearchesResponse {
  pagination: {
    current_page: number;
    last_page: number;
    total: number;
  };
  searches: RecentSearchResult[];
}

export async function getRecentSearches(
  token: string,
  page: number = 1,
  per_page: number = 5,
  type: string = "recent",
): Promise<RecentSearchesResponse> {
  try {
    const response = await fetchWithToken(
      `${apiServer}/user/searches?type=${type}&page=${page}&per_page=${per_page}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const data = await response?.json();

    // Check if the response has the expected structure
    if (data?.status === "success" && data?.data) {
      return {
        searches: data.data.searches || [],
        pagination: data.data.pagination || {
          current_page: page,
          last_page: 1,
          per_page: per_page,
          total: 0,
          from: 0,
          to: 0,
        },
      };
    }

    // Return empty data with default pagination if request failed
    return {
      searches: [],
      pagination: {
        current_page: page,
        last_page: 1,
        total: 0,
      },
    };
  } catch (error) {
    console.error("Error fetching recent searches:", error);
    // Return empty data with default pagination on error
    return {
      searches: [],
      pagination: {
        current_page: page,
        last_page: 1,
        total: 0,
      },
    };
  }
}

export async function addRecentSearch(
  search: {
    searchTerm: string;
    types: string;
    accessibilities: string;
    showVenueOptions: string;
    openingHoursDay: string;
    openingHoursStartTime: string;
    openingHoursEndTime: string;
    // New geocoding parameters
    latitude?: string;
    longitude?: string;
    address?: string;
    // New search context parameters
    search_type?: string;
    search_value?: string;
    tagging_mode?: string;
    placesToReplaceString?: string;
  },
  token: string,
) {
  const formData = new FormData();

  formData.append("search_term", search.searchTerm);
  formData.append("types", search.types);
  formData.append("accessibilities", search.accessibilities);
  formData.append("showVenueOptions", search.showVenueOptions);
  formData.append("openingHoursDay", search.openingHoursDay);
  formData.append("openingHoursStartTime", search.openingHoursStartTime);
  formData.append("openingHoursEndTime", search.openingHoursEndTime);

  // Add new geocoding parameters
  if (search.latitude) formData.append("latitude", search.latitude);
  if (search.longitude) formData.append("longitude", search.longitude);
  if (search.address) formData.append("address", search.address);

  // Add new search context parameters
  if (search.search_type) formData.append("search_type", search.search_type);
  if (search.search_value) formData.append("search_value", search.search_value);
  if (search.tagging_mode) formData.append("tagging_mode", search.tagging_mode);
  if (search.placesToReplaceString) formData.append("placesToReplaceString", search.placesToReplaceString);

  console.log("addRecentSearch", formData);
  const response = await fetchWithToken(`${apiServer}/user/searches/recent`, {
    method: "POST",
    body: formData,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response?.json();
  if (data.status !== "success")
    return { success: false, message: data.message };
  return { success: true, message: data.message };
}
